import 'core-js/features/promise';
import '@department-of-veterans-affairs/component-library/dist/main.css';
import {
  applyPolyfills,
  defineCustomElements,
} from '@department-of-veterans-affairs/component-library';
import { loadWithRetry } from '@department-of-veterans-affairs/platform-utilities/lazy-load-with-retry';
import { reloadIfBuildStale } from '@department-of-veterans-affairs/platform-utilities/stale-build-detector';

// Contract shared with the component-library loader (retryDynamicImport /
// module-load-error in @department-of-veterans-affairs/web-components). The
// loader reads the resolver off this global on retry and dispatches the event
// on a terminal failure. Keep these strings in sync with component-library.
const CHUNK_URL_RESOLVER_KEY = '__VA_CHUNK_URL_RESOLVER__';
const VA_COMPONENT_LOAD_ERROR_EVENT = 'vaComponentLoadError';
// Global assigned by the build-time manifest script (see
// config/esbuild/config.js buildWebComponents) and the URL it is served from.
// Read via globalThis (same object as window in the browser) to match the
// resolver global below.
//
// The URL is build-time configurable because font-variant bundles (see
// buildWebComponents' `fontVariant` option) emit their own manifest under a
// different filename. Without this, a variant bundle would load the default
// build's manifest and, on a chunk retry, silently fetch a wrongly-styled
// chunk. Every esbuild pass that includes this module defines the value, so
// the `||` fallback only applies under Node (unit tests).
const CHUNK_MANIFEST_GLOBAL = '__VA_WC_CHUNK_MANIFEST__';
const CHUNK_MANIFEST_URL =
  process.env.WC_CHUNK_MANIFEST_URL || '/generated/wc-chunk-manifest.js';

/**
 * Normalizes a Stencil lazy-load specifier to its manifest key.
 *
 * The loader hands us specifiers like `./va-alert_2.entry.js` (with a cache-bust
 * query on later retries); the manifest is keyed by basename.
 */
export function specifierToKey(specifier) {
  if (!specifier) {
    return null;
  }
  return String(specifier).split('?')[0].split('/').pop();
}

/**
 * Loads the build-time chunk manifest (a script assigning
 * window.__VA_WC_CHUNK_MANIFEST__) and resolves to it, or to null if it is
 * absent or fails to load. Loading it here rather than via an HTML <script> keeps
 * this a self-contained vets-website change with no coordinated content-build
 * deploy; a null manifest simply leaves the loader's prior behavior in place.
 */
function loadChunkManifest() {
  if (typeof document === 'undefined') {
    return Promise.resolve(null);
  }
  if (globalThis[CHUNK_MANIFEST_GLOBAL]) {
    return Promise.resolve(globalThis[CHUNK_MANIFEST_GLOBAL]);
  }
  return new Promise(resolve => {
    const script = document.createElement('script');
    script.src = CHUNK_MANIFEST_URL;
    script.async = false;
    script.onload = () => resolve(globalThis[CHUNK_MANIFEST_GLOBAL] || null);
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
}

/**
 * Installs the chunk-URL resolver the component-library loader consults on
 * retry. Given a failed specifier it returns the real emitted chunk URL so the
 * loader can cache-bust that URL and re-fetch it — the only way to recover a
 * cached-failed chunk under esbuild (which renamed the specifier) and in Safari
 * (whose import error carries no URL). "Cached-failed" = the browser has cached
 * the rejected import() so re-importing the same URL returns the failure with no
 * network request; a fresh (cache-busted) URL is required to recover.
 */
export function installChunkUrlResolver(manifest) {
  if (!manifest) {
    return;
  }
  globalThis[CHUNK_URL_RESOLVER_KEY] = specifier =>
    (specifier && manifest[specifierToKey(specifier)]) || null;
}

/**
 * Forwards a `vaComponentLoadError` event to DataDog RUM. Terminal failures
 * (retries and in-place recovery both exhausted) carry
 * `{ tagName, moduleUrl, attempts, error }`; when `error` is missing we
 * synthesize one tagged with the component so the RUM entry is still
 * identifiable.
 *
 * `addError` is injectable so tests can assert on it directly without mutating
 * the `window.DD_RUM` global (which the module bootstrap also writes to); it
 * defaults to the real RUM reporter and is a no-op when RUM is unavailable.
 */
export function reportComponentLoadError(
  event,
  addError = (...args) => window.DD_RUM?.addError?.(...args),
) {
  const detail = (event && event.detail) || {};
  const error =
    detail.error instanceof Error
      ? detail.error
      : new Error(`vaComponentLoadError: ${detail.tagName || 'unknown'}`);
  addError(error, {
    source: 'wc-loader',
    wcTagName: detail.tagName,
    wcModuleUrl: detail.moduleUrl,
    wcAttempts: detail.attempts,
  });
}

/**
 * Handles a terminal per-component load failure: this is the terminal
 * signal that produces "Constructor for X#undefined was not found" once
 * Cstr resolves falsy in initializeComponent. First confirms whether the
 * running build is actually stale (a tab left open across a deploy,
 * referencing chunk hashes the CDN has since rotated out) — the one case a
 * reload actually fixes, since no amount of retrying can recover a chunk
 * that no longer exists. When confirmed, silently reload instead of
 * reporting: it's an expected, self-recovering condition, not a bug to
 * alert on. Otherwise (a genuine, non-stale failure) report as before.
 * reloadIfBuildStale never throws, so it can't mask the RUM report.
 * @returns {Promise<void>} Resolves once handled; browsers ignore an event
 *   listener's return value, this is solely for tests to await.
 */
export async function handleComponentLoadError(event) {
  const recovered = await reloadIfBuildStale();
  if (!recovered) {
    reportComponentLoadError(event);
  }
}

/**
 * Reports terminal web-component load failures (retries and in-place recovery
 * both exhausted) to DataDog RUM, so we can monitor how often a chunk stays
 * broken. Registered synchronously so it catches failures that occur before
 * init resolves.
 */
function installLoadErrorReporter() {
  if (typeof document === 'undefined') {
    return;
  }
  document.addEventListener(
    VA_COMPONENT_LOAD_ERROR_EVENT,
    handleComponentLoadError,
  );
}

/**
 * Initialize web components with retry logic for transient network failures.
 *
 * DataDog RUM analysis (100k+ errors/week) shows chunk load requests
 * complete with no HTTP status code, indicating network interruption.
 * Retrying with exponential backoff recovers from these transient failures.
 *
 * Before defining components we install the chunk-URL resolver so the loader can
 * recover a permanently cached-failed chunk in place (component-library RC
 * 56.10.1-rc). The manifest load is best-effort and deliberately does NOT gate
 * defineCustomElements: the manifest <script> has no timeout, and a stalled (not
 * failed) connection would otherwise block all web-component hydration site-wide
 * until the browser's network timeout. The resolver is a pure enhancement
 * consulted only on a chunk-load retry, so if the manifest arrives after a chunk
 * has already failed, that early failure simply falls back to the prior retry
 * behavior ("never worse than today").
 */
async function initWebComponents() {
  // Fire-and-forget: install the resolver whenever the manifest arrives, without
  // blocking hydration on it. loadChunkManifest never rejects (resolves null on
  // failure) and installChunkUrlResolver no-ops on a null manifest.
  loadChunkManifest().then(installChunkUrlResolver);
  await applyPolyfills();
  await defineCustomElements();
}

// Don't initialize web components on saved pages because this causes the
// content to become hidden. It does end up breaking the styling, but this is
// better than not being able to see the internal content
if (window.location?.protocol !== 'file:') {
  installLoadErrorReporter();
  loadWithRetry(initWebComponents, 3, 1000, 10000).catch(error =>
    window.DD_RUM?.addError(error, { source: 'wc-loader' }),
  );
}
